import { useState, useEffect } from 'react';
import { Bell, BellSlash, BellRinging, X } from '@phosphor-icons/react';
import { usePushNotifications } from '../../hooks/usePushNotifications';

export const NotificationBanner = () => {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('notif_banner_dismissed') === 'true'
  );
  const [autoHidden, setAutoHidden] = useState(false);

  // Auto-hide after 8 seconds
  useEffect(() => {
    if (!subscribed && !dismissed) {
      const timer = setTimeout(() => setAutoHidden(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [subscribed, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('notif_banner_dismissed', 'true');
  };

  if (!supported || dismissed || autoHidden) return null;

  // Already subscribed — show compact status briefly
  if (subscribed) {
    return (
      <div className="flex items-center justify-between bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl px-4 py-2.5" data-testid="notification-active">
        <div className="flex items-center gap-2">
          <BellRinging size={14} className="text-[#16a34a]" weight="fill" />
          <span className="text-xs text-[#16a34a] font-medium">Notifications active</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendTest} className="text-[10px] text-[#16a34a] hover:underline font-medium" data-testid="test-notification-btn">Test</button>
          <button onClick={unsubscribe} disabled={loading} className="text-[10px] text-[#596065] hover:text-[#ef4444] transition-colors" data-testid="disable-notifications-btn">Turn off</button>
          <button onClick={handleDismiss} className="p-0.5 text-[#16a34a]/50 hover:text-[#16a34a] transition-colors" data-testid="dismiss-notif-banner"><X size={14} weight="bold" /></button>
        </div>
      </div>
    );
  }

  // Permission denied — dismissible
  if (permission === 'denied') {
    return (
      <div className="flex items-center justify-between bg-[#fef2f2] border border-[#fecaca] rounded-xl px-4 py-2.5" data-testid="notification-denied">
        <div className="flex items-center gap-2.5">
          <BellSlash size={14} className="text-[#dc2626]" weight="duotone" />
          <div>
            <p className="text-xs text-[#dc2626] font-medium">Notifications blocked</p>
            <p className="text-[10px] text-[#dc2626]/60">Enable in browser settings for due date reminders</p>
          </div>
        </div>
        <button onClick={handleDismiss} className="p-1 text-[#dc2626]/40 hover:text-[#dc2626] transition-colors rounded-lg hover:bg-[#dc2626]/10" data-testid="dismiss-notif-banner">
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  // Not subscribed — opt-in with dismiss
  return (
    <div className="flex items-center justify-between bg-gradient-to-r from-[#0f172a] to-[#1e293b] rounded-xl px-4 py-2.5" data-testid="notification-optin">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-[#f59e0b]/20 flex items-center justify-center">
          <Bell size={14} className="text-[#f59e0b]" weight="fill" />
        </div>
        <div>
          <p className="text-xs text-white font-medium">Never miss a due date</p>
          <p className="text-[10px] text-[#94a3b8]">Get notified 24h before and on the day</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={subscribe}
          disabled={loading}
          className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-[#f59e0b] text-[#0f172a] hover:bg-[#fbbf24] transition-colors disabled:opacity-50"
          data-testid="enable-notifications-btn"
        >
          {loading ? 'Enabling...' : 'Enable'}
        </button>
        <button onClick={handleDismiss} className="p-1 text-white/30 hover:text-white/70 transition-colors" data-testid="dismiss-notif-banner">
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
};
